import { AuthPageHeader } from "@/features/auth/components/layout";
import useInitiateFreemiumSignupMutation from "@/features/auth/hooks/useInitiateFreemiumSignupMutation";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { zodResolver } from "@hookform/resolvers/zod";
import { ui } from "@patient-watch/ui";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type FormSchemaType = z.infer<typeof formSchema>;

export function FreemiumSignupScreen({ email: searchEmail }: { email?: string }) {
    const copy = useAuthCopy();
    const signupMutation = useInitiateFreemiumSignupMutation();
    const [progressMessage, setProgressMessage] = useState("");

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: searchEmail ?? "",
        },
    });

    const onSubmit = form.handleSubmit(async (values) => {
        setProgressMessage("");
        try {
            await signupMutation.mutateAsync({
                email: values.email.trim(),
                onProgress: setProgressMessage,
            });
        } catch {
            setProgressMessage("");
        }
    });

    const isBusy = signupMutation.isPending;

    return (
        <div className="space-y-6 w-full">
            <AuthPageHeader title={copy.freemiumSignupTitle} description={copy.freemiumSignupDescription} />

            <ui.Card className="bg-gradient-to-br from-emerald-50/60 dark:from-emerald-950/20 to-white dark:to-slate-900 border-emerald-200 dark:border-emerald-800">
                <ui.CardContent className="space-y-2 pt-6 text-muted-foreground text-sm">
                    <div className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 size-4 text-emerald-600 shrink-0" />
                        <span>{copy.freemiumSignupBenefit1}</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 size-4 text-emerald-600 shrink-0" />
                        <span>{copy.freemiumSignupBenefit2}</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 size-4 text-emerald-600 shrink-0" />
                        <span>{copy.freemiumSignupBenefit3}</span>
                    </div>
                </ui.CardContent>
            </ui.Card>

            <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                    <ui.Label htmlFor="freemium-email">{copy.email}</ui.Label>
                    <ui.Input
                        id="freemium-email"
                        type="email"
                        autoComplete="email"
                        placeholder={copy.emailPlaceholder}
                        disabled={isBusy}
                        data-testid="freemium-email-input"
                        {...form.register("email")}
                    />
                    {form.formState.errors.email ? (
                        <p className="text-destructive text-sm" role="alert">
                            {form.formState.errors.email.message}
                        </p>
                    ) : null}
                </div>

                {signupMutation.isError ? (
                    <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/40 px-3 py-2 border border-red-200 dark:border-red-900 rounded-md text-red-800 dark:text-red-100 text-sm">
                        <AlertCircle className="mt-0.5 size-4 shrink-0" />
                        <span>
                            {signupMutation.error instanceof Error
                                ? signupMutation.error.message
                                : copy.somethingWentWrong}
                        </span>
                    </div>
                ) : null}

                {progressMessage ? (
                    <p className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Loader2 className="size-4 animate-spin" />
                        {progressMessage}
                    </p>
                ) : null}

                <ui.Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isBusy}
                    data-testid="freemium-signup-button"
                >
                    {isBusy ? (
                        <>
                            <Loader2 className="mr-2 size-4 animate-spin" />
                            {copy.freemiumSignupSubmitting}
                        </>
                    ) : (
                        copy.freemiumSignupCta
                    )}
                </ui.Button>
            </form>

            <p className="text-muted-foreground text-sm text-center">
                {copy.freemiumSignupUpgradeHint}{" "}
                <Link
                    to="/signup/$plan"
                    params={{ plan: "starter" }}
                    search={{ canceled: false, email: searchEmail ?? "" }}
                    className="font-medium text-foreground underline"
                >
                    {copy.freemiumSignupSeePaidPlans}
                </Link>
            </p>
        </div>
    );
}
